import { IDiagnosisBlock, InspectionStatus, TreatmentStatus } from 'models'

export interface IDailyStatisticsState {
	issues: {
		repeat_identification: boolean
	}
	identified_count: {
		value: number
		out_of: number
		unidentified: number
	}
	milking_data: {
		today_total: number
		today_average: number
		yesterday_at_this_time: number
		yesterday_at_this_time_average: number
		yesterday_average: number
		yesterday_total: number
	}
	fatness_data: IDonutChart[]
	limpness_data: IDonutChart[]
}

export interface IDonutChart {
	label: string
	color: string
	value: number
	previous_value?: number | null
	prev_value?: number | null
}

export interface IDigitizatedCowInfo {
	id: number
	cow: {
		id: number
		tag_id: number
		images: {
			id: number
			image: string
			timestamp: number
			angle: 'up' | 'side' | 'back' | 'tag'
		}[]
	}
	camera: number
	on_pass_through: boolean
	created_dt: number
	images: {
		id: number
		image: string
	}[]
}

export type postTagInfoType = {
	image: File | string | FormData
	timestamp: number
	angle: string
	cow_id: number
}
export type patchCowInfoType = {
	cow_id: number
	tag_id?: number
	farm_cow_number?: number
	name?: string
	birth_date?: string
	weight?: number
	height?: number
	chest_width?: number
	trunk_depth?: number
	sacrum_length?: number
	sacrum_angle?: number
	pelvic_width?: number
	legs_side?: number
	legs_behind?: number
	fatness?: number
	limpness?: number
}

export interface IUpdateLamenessScoreRequest {
	cow_id: number
	limpness_score: number
	tracking_id?: number | null
}

export interface IUpdateLamenessScoreResponse {
	cow_id: number
	limpness: number
	limpness_confirmation: number | null
}

export interface ICowsList {
	id: number
	breed: {
		id: number
		name: string
	}
	group: {
		id: number
		title: string
	}
	identified: boolean
	tag_id: number
	farm_cow_number: number
	name: string
	birth_date: number
	weight: number
	height: number
	collar_num?: string
	chest_width: number
	trunk_depth: number
	sacrum_length: number
	sacrum_angle: number
	pelvic_width: number
	legs_side: number
	legs_behind: number
	ration: {
		id: number
		title: string
	}
	images: {
		id: number
		image: string
		timestamp: number
		angle: 'up' | 'down' | 'left' | 'right'
	}[]
	today_milking_amount: string
	last_milking_dt: string
	last_milking_amount: number
	fatness: number
	fatness_status: 'attention' | 'critical' | 'ok'
	limpness: number
	lactation_length: number
	lactation_count: string
	critical_state: boolean
	lactation_state: string | null
	fatness_norm: string
	limpness_length: string | null
	limpness_start_dt: number | null
	limpness_change_dt: number | null
	limpness_score: number | null
	limpness_score_is_false: boolean
	limpness_score_manual: number | null
	limpness_score_manual_by: string | null
	// видео, фото и дата — с последнего замера камеры «Выход (сбоку)»
	limpness_video_url: string | null
	limpness_side_photo_url: string | null
	last_limpness_tracking_id: number | null
	last_limpness_tracking_dt: number | null
	// замер, двухфакторно подтвердивший балл limpness
	limpness_tracking_id: number | null
	limpness_tracking_dt: number | null
	diagnosis: IDiagnosisBlock[] | null
	treatment_status: TreatmentStatus | null
	treatment_color: string | null
	examination_status: InspectionStatus | null
	treatment_length: string | null
	first_examination_date: number | null
	next_examination_date: number | null
	milk_status: string | null
	examiner: string | null
	trim_last_dt: number | null
	trim_next_dt: number | null
	trim_responsible_first_name: string | null
	trim_responsible_last_name: string | null
	trim_responsible_position: string | null
}

export interface ICowLightInfo {
	id: number
	group: {
		id: number
		title: string
	}
	tag_id: number | string
	collar_num?: string | null
	name: string
	birth_date: number
	ration: {
		id: number
		title: string
		dry_matter_total: number
	}
	images: {
		id: number
		image: string
		timestamp: number
		angle: 'up' | 'down' | 'left' | 'right'
	}[]
	today_milking_amount: string
	lactation_length: number
	lactation_count: number
	fatness: number
	fatness_change_dt: number
	limpness: number
	limpness_change_dt: number
	yield_status: 'ok' | 'dry' | 'rejected'
	treatment_status: TreatmentStatus | null
	fatness_status: 'attention' | 'critical' | 'ok'
	identified?: boolean
}

export interface ICowsListResponse {
	count: number
	dispose_recommended: number
	examination_overdue: number
	examination_required: number
	trim_missed: number
	next: string | null
	previous: string | null
	results: ICowsList[]
}

export interface ICowYieldsGraphInfo {
	date: string
	milk_sum: number
	milkings: {
		timestamp: number
		milk_amount: number
	}[]
}

export interface ICowGraphResponse {
	total_milk_sum: number
	graph: ICowYieldsGraphInfo[]
}

export interface ILamenessGraphResponse extends IFatnessGraphResponse {
	limp_count: number
	healthy_count: number
}

export interface IFatnessGraphResponse {
	lines: ILineGraph[]
}

export interface ILineGraph {
	dots: IDot[]
	label: string
	score: number
}

export interface IDot {
	date?: string
	date_ms: number
	value: number
}

export interface IAvailableFilterValuesResponse {
	limpness: {
		start: number | ''
		end: number | ''
	}
	treatment: {
		start: number | ''
		end: number | ''
	}
	examination: {
		start: number | ''
		end: number | ''
	}
	age: {
		start: number | ''
		end: number | ''
	}
	lactation: {
		start: number | ''
		end: number | ''
	}
	last_trim: {
		start: number | ''
		end: number | ''
	}
}

export interface ITrimsBatching {
	target_date: string
	responsible: {
		firstname?: string
		lastname?: string
		role_title?: string
		id: number
	}
	trim_ids?: number[]
}

export interface ITrims {
	id: number
	created_dt: number
	cow: {
		id: number
		tag_id: number
		images: {
			id: number
			image: string
			timestamp: number
			angle: 'up' | 'side' | 'back' | 'tag'
		}[]
	}
	target_date: number
	responsible: {
		firstname: string
		lastname: string
		role_title: string
	}
	done_dt: number
	comment: string
	next_trim_dt: number
	closest_examination: {
		id: number
		created_dt: number
		examiner: {
			firstname: string
			lastname: string
			role_title: string
		}
		diagnosis: IDiagnosisBlock[]
		next_examination_date: number
		milk_reject_start: number
		milk_reject_end: number
		dispose_recommended: boolean
		limpness: number
	}
}

export interface IHoofHealthTable {
	date: number
	limpness: number
	event: 'Лечение' | 'Хромота' | 'Обрезка' | 'Выбытие'
	diagnosis: IDiagnosisBlock[]
	examiner: string
	comment?: string
	treatment_plan?: string | null
	treatment_duration?: number | null
}

export interface IFatnessHealthTable {
	date: number
	group_title: string
	ration_title: string
	fatness: number
	critical: boolean
	fatness_norm: string
	psv: number
}

export interface IPayloadCattleStatuses {
	yieldStatus: 'ok' | 'dry' | 'rejected'
	treatmentStatus: TreatmentStatus | InspectionStatus
	fatnessStatus: 'attention' | 'critical' | 'ok'
	identified?: boolean
}

export interface IIdentificationProps {
	rec_date: number
	camera_id: number
	day_period: ShiftOptions
	page: number
	page_size: number
}

export interface IIdentificationResponse {
	count: number
	next: string
	previous: string
	results: IIdentificationData[]
}

export interface IIdentificationData {
	id: number
	cow_id: number
	tag_id: string
	initial_cow_tag: string
	day_period?: ShiftOptions
	manually_identified: boolean
	images: [
		{
			id: number
			image: string
		},
	]
}
export enum ShiftOptions {
	AM = 'am',
	PM = 'pm',
	NIGHT = 'night',
}

export enum ShiftStatuses {
	CLOSED = 'closed',
	PENDING = 'pending',
	FINISHED = 'finished',
}
export interface IPatchChangeTagIdProps {
	tracking_id: number
	body: IPatchChangeTagIdBody
}

export interface IPatchChangeTagIdBody {
	cow_id: number
	tag_id: string
	rec_date: number
	camera_id: number
	day_period: ShiftOptions
}
export interface ITreatmentSchemaSickness {
	id: number
	title: string
	group?: string
}

export interface ITreatmentSchema {
	id: number
	title?: string
	sickness?: ITreatmentSchemaSickness | ITreatmentSchemaSickness[] | null
	sicknesses?: ITreatmentSchemaSickness | ITreatmentSchemaSickness[] | null
	sickness_id?: number | number[] | null
	sickness_ids?: number | number[] | null
	sicknesses_id?: number | number[] | null
	duration: number // Количество дней
	description: string // Схема лечения
	examination_interval?: number | null
	exam_interval_days?: number | null
	inspection_interval?: number | null
	milk_reject_days?: number | null
}

export interface ITreatmentSchemaOptionsResponse {
	items: ITreatmentSchemaOptions[]
}

export interface ITreatmentSchemaOptions {
	id: number
	title: string
	group: string
}

export interface IAvailableCowTags {
	tags: number[]
}
